import React, { useRef, useState, useEffect, useCallback } from 'react';
import '@platform/app-shell/frontend/components/PullToRefresh.css';

const THRESHOLD = 80; // px to pull before refresh triggers
const MAX_PULL = 120; // max visual pull distance

export default function PullToRefresh({ children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const containerRef = useRef(null);

  // Check if the page is scrolled to the top
  const isAtTop = useCallback(() => {
    // Walk up from the touch target to find the nearest scrollable ancestor
    // If all scrollable ancestors are at scrollTop 0, we're at the top
    const scrollables = containerRef.current?.querySelectorAll('*');
    if (!scrollables) return true;
    for (const el of scrollables) {
      if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight) {
        // Check if this element is vertically scrollable and not at top
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return false;
        }
      }
    }
    return true;
  }, []);

  const onTouchStart = useCallback((e) => {
    if (refreshing) return;
    touchStartY.current = e.touches[0].clientY;
    isPulling.current = false;
  }, [refreshing]);

  const onTouchMove = useCallback((e) => {
    if (refreshing) return;
    const currentY = e.touches[0].clientY;
    const delta = currentY - touchStartY.current;

    // Only start pulling if swiping down and content is at top
    if (delta > 10 && !isPulling.current) {
      if (!isAtTop()) return;
      isPulling.current = true;
    }

    if (isPulling.current && delta > 0) {
      // Dampen the pull with a diminishing curve
      const dampened = Math.min(delta * 0.4, MAX_PULL);
      setPullDistance(dampened);
      e.preventDefault();
    }
  }, [refreshing, isAtTop]);

  const onTouchEnd = useCallback(() => {
    if (!isPulling.current) return;
    isPulling.current = false;

    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD * 0.5);
      // Small delay so the spinner is visible before reload
      setTimeout(() => window.location.reload(), 300);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance]);

  // Only attach listeners on touch devices
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  if (!isTouchDevice) return children;

  return (
    <div
      ref={containerRef}
      className="pull-to-refresh-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className={`pull-to-refresh-indicator ${pullDistance > 0 || refreshing ? 'visible' : ''} ${refreshing ? 'refreshing' : ''}`}
        style={{ height: pullDistance }}
      >
        <div className={`pull-to-refresh-icon ${pullDistance >= THRESHOLD ? 'ready' : ''}`}>
          {refreshing ? (
            <div className="pull-to-refresh-spinner" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: `rotate(${Math.min(pullDistance / THRESHOLD, 1) * 180}deg)`, transition: 'transform 0.1s' }}>
              <polyline points="7 13 12 18 17 13" />
              <line x1="12" y1="6" x2="12" y2="18" />
            </svg>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
